import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { apiCreateRsvp, apiGetEventBySlug } from '../../services/api';
import { validateOptionalEmail } from '../../utils/validators';

function toDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleString('pt-BR', {
    dateStyle: 'full',
    timeStyle: 'short',
  });
}

function getCountdownParts(targetDate) {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) {
    return { done: true, days: 0, hours: 0, minutes: 0 };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);

  return { done: false, days, hours, minutes };
}

export default function InvitePublic() {
  const { slug } = useParams();
  const [eventData, setEventData] = useState(null);
  const [summary, setSummary] = useState({ confirmed: 0, pending: 0, declined: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [rsvpReceipt, setRsvpReceipt] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [autoPdfProtocol, setAutoPdfProtocol] = useState('');
  const [countdown, setCountdown] = useState({ done: false, days: 0, hours: 0, minutes: 0 });

  const [formData, setFormData] = useState({
    guestName: '',
    guestEmail: '',
    guestWhatsapp: '',
    status: 'confirmed',
    companions: 0,
    companionDetails: [],
    dietaryRestrictions: '',
    wantsReminder: true,
    lgpdConsent: false,
  });

  const backgroundStyle = useMemo(() => {
    const image = eventData?.background_image_url;
    if (!image) return {};

    return {
      backgroundImage: `linear-gradient(120deg, rgba(255,107,107,0.72), rgba(78,205,196,0.7)), url(${image})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }, [eventData]);

  const maxCompanionsAllowed = useMemo(() => {
    const maxGuests = Number(eventData?.max_guests || 1);
    if (!Number.isFinite(maxGuests) || maxGuests <= 1) return 0;
    return maxGuests - 1;
  }, [eventData]);

  const qrPayload = useMemo(() => {
    if (!rsvpReceipt || !eventData) return '';

    return JSON.stringify({
      protocol: rsvpReceipt.protocol,
      guestName: rsvpReceipt.guestName,
      eventSlug: slug,
      eventTitle: eventData.title,
      status: rsvpReceipt.status,
      checkedAt: new Date().toISOString(),
    });
  }, [rsvpReceipt, eventData, slug]);

  const qrCodeUrl = useMemo(() => {
    if (!qrPayload) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(qrPayload)}`;
  }, [qrPayload]);

  useEffect(() => {
    async function loadEvent() {
      setLoading(true);
      const data = await apiGetEventBySlug(slug);

      if (data.error) {
        setError(data.error);
      } else {
        setEventData(data.event);
        setSummary(data.summary || { confirmed: 0, pending: 0, declined: 0 });
      }

      setLoading(false);
    }

    loadEvent();
  }, [slug]);

  useEffect(() => {
    if (!eventData?.event_date) return;

    const updateCountdown = () => setCountdown(getCountdownParts(eventData.event_date));
    updateCountdown();

    const timer = setInterval(updateCountdown, 30000);
    return () => clearInterval(timer);
  }, [eventData]);

  const onChange = (field) => (event) => {
    const value = field === 'wantsReminder' || field === 'lgpdConsent'
      ? event.target.checked
      : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompanionsChange = (event) => {
    const numericValue = Number(event.target.value);
    const normalized = Number.isNaN(numericValue)
      ? 0
      : Math.max(0, Math.min(maxCompanionsAllowed, Math.floor(numericValue)));

    setFormData((prev) => {
      const nextCompanions = normalized;
      const nextDetails = Array.from({ length: nextCompanions }, (_, index) => {
        const existing = prev.companionDetails[index];
        return {
          name: existing?.name || '',
          email: existing?.email || '',
        };
      });

      return {
        ...prev,
        companions: nextCompanions,
        companionDetails: nextDetails,
      };
    });
  };

  const handleCompanionDetailChange = (index, field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      companionDetails: prev.companionDetails.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: value } : item
      )),
    }));
  };

  const handleDownloadReceiptPdf = async () => {
    if (!rsvpReceipt || !eventData || isGeneratingPdf) return;

    setIsGeneratingPdf(true);

    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const marginX = 48;
      let cursorY = 62;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(21);
      doc.text('Comprovante de Presenca - Festify', marginX, cursorY);

      cursorY += 28;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`Emitido em: ${new Date().toLocaleString('pt-BR')}`, marginX, cursorY);

      cursorY += 30;
      doc.setDrawColor(212, 222, 235);
      doc.roundedRect(marginX, cursorY, 500, 185, 10, 10);

      cursorY += 24;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(`Protocolo: ${rsvpReceipt.protocol}`, marginX + 16, cursorY);

      cursorY += 24;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.text(`Convidado: ${rsvpReceipt.guestName}`, marginX + 16, cursorY);
      cursorY += 20;
      doc.text(`Status: ${rsvpReceipt.status}`, marginX + 16, cursorY);
      cursorY += 20;
      doc.text(`Total de pessoas: ${rsvpReceipt.attendees}`, marginX + 16, cursorY);
      cursorY += 20;
      doc.text(`Evento: ${eventData.title}`, marginX + 16, cursorY);
      cursorY += 20;
      doc.text(`Data: ${formatDate(eventData.event_date)}`, marginX + 16, cursorY);

      if (qrCodeUrl) {
        try {
          const qrResponse = await fetch(qrCodeUrl);
          const qrBlob = await qrResponse.blob();
          const qrDataUrl = await toDataUrl(qrBlob);

          doc.setFontSize(10);
          doc.text('QR de confirmacao para validacao na entrada', marginX, 314);
          doc.addImage(qrDataUrl, 'PNG', marginX, 328, 170, 170);
        } catch (_error) {
          doc.setFontSize(10);
          doc.text('Nao foi possivel incorporar o QR Code neste PDF.', marginX, 350);
        }
      }

      doc.setFontSize(10);
      doc.text('Apresente este comprovante digital no acesso ao evento.', marginX, 525);

      const fileSafeProtocol = String(rsvpReceipt.protocol || 'comprovante').replace(/[^a-zA-Z0-9-_]/g, '-');
      doc.save(`comprovante-${fileSafeProtocol}.pdf`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    if (!rsvpReceipt || rsvpReceipt.status !== 'confirmed') return;
    if (autoPdfProtocol === rsvpReceipt.protocol) return;

    setAutoPdfProtocol(rsvpReceipt.protocol);
    handleDownloadReceiptPdf();
  }, [rsvpReceipt, autoPdfProtocol]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    const payload = {
      ...formData,
      companions: Number(formData.companions || 0),
      companionDetails: formData.companionDetails
        .slice(0, Number(formData.companions || 0))
        .map((item) => ({
          name: (item.name || '').trim(),
          email: (item.email || '').trim(),
        })),
    };

    const emailError = validateOptionalEmail(payload.guestEmail);
    if (emailError) {
      setError(emailError);
      return;
    }

    if (payload.companions > maxCompanionsAllowed) {
      setError(`Para este evento, o máximo é ${maxCompanionsAllowed} acompanhante(s).`);
      return;
    }

    const missingCompanionName = payload.companionDetails.some((item) => !item.name);
    if (missingCompanionName) {
      setError('Preencha o nome de todos os acompanhantes.');
      return;
    }

    const data = await apiCreateRsvp(slug, payload);
    if (data.error) {
      setError(data.error);
      return;
    }

    setSummary(data.summary || summary);
    setRsvpReceipt({
      protocol: data.rsvp?.rsvp_protocol || 'sem-protocolo',
      guestName: payload.guestName,
      status: payload.status,
      attendees: Number(payload.companions || 0) + 1,
      submittedAt: new Date().toISOString(),
    });
    setMessage(`Presença registrada com sucesso. Protocolo: ${data.rsvp?.rsvp_protocol || 'sem-protocolo'}`);

    if (payload.status === 'confirmed') {
      setShowConfetti(true);
      window.setTimeout(() => setShowConfetti(false), 1400);
    }
  };

  if (loading) {
    return <p className="loading-text">Carregando convite...</p>;
  }

  if (error && !eventData) {
    return <p className="api-error">{error}</p>;
  }

  return (
    <main className="invite-page" style={backgroundStyle}>
      <header className="invite-header">
        <h2><Link to="/">Festify</Link></h2>
      </header>

      <section className="invite-card">
        <p className="festify-eyebrow">Convite oficial</p>
        <h1>{eventData.title}</h1>
        <p className="invite-meta">{formatDate(eventData.event_date)} • {eventData.location}</p>
        {eventData.theme && <p className="theme-badge">Tema: {eventData.theme}</p>}
        {eventData.description && <p className="invite-description">{eventData.description}</p>}

        <div className="countdown-box">
          {countdown.done ? (
            <p>O evento já começou. Aproveite!</p>
          ) : (
            <p>Faltam {countdown.days} dias, {countdown.hours} horas e {countdown.minutes} minutos para a festa.</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="invite-form">
          {error && <p className="api-error">{error}</p>}
          {message && <p className="api-success">{message}</p>}

          <label>
            Seu nome
            <input required value={formData.guestName} onChange={onChange('guestName')} />
          </label>

          <div className="grid-two">
            <label>
              Seu email (opcional)
              <input type="email" value={formData.guestEmail} onChange={onChange('guestEmail')} />
            </label>
            <label>
              WhatsApp (opcional)
              <input type="text" placeholder="+55..." value={formData.guestWhatsapp} onChange={onChange('guestWhatsapp')} />
            </label>
          </div>

          <div className="grid-two">
            <label>
              Status
              <select value={formData.status} onChange={onChange('status')}>
                <option value="confirmed">Confirmado</option>
                <option value="pending">Pendente</option>
                <option value="declined">Recusado</option>
              </select>
            </label>
            <span />
          </div>

          <div className="grid-two">
            <label>
              Vou levar quantas pessoas?
              <input
                type="number"
                min="0"
                max={maxCompanionsAllowed}
                value={formData.companions}
                onChange={handleCompanionsChange}
              />
              <small className="input-helper">Máximo permitido pelo organizador: {maxCompanionsAllowed}</small>
            </label>
            <label>
              Restrições alimentares
              <input value={formData.dietaryRestrictions} onChange={onChange('dietaryRestrictions')} />
              <small className="input-helper">Opcional</small>
            </label>
          </div>

          {formData.companions > 0 && (
            <div className="companions-section">
              <p className="companions-title">Dados dos acompanhantes</p>
              <div className="companions-list">
                {formData.companionDetails.map((companion, index) => (
                  <div className="grid-two" key={`companion-${index}`}>
                    <label>
                      Nome do acompanhante {index + 1}
                      <input
                        required
                        value={companion.name}
                        onChange={handleCompanionDetailChange(index, 'name')}
                      />
                    </label>
                    <label>
                      Email do acompanhante {index + 1} (opcional)
                      <input
                        type="email"
                        value={companion.email}
                        onChange={handleCompanionDetailChange(index, 'email')}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <label className="checkbox-row toggle-row">
            <input
              className="toggle-input"
              type="checkbox"
              checked={formData.wantsReminder}
              onChange={onChange('wantsReminder')}
            />
            <span className="toggle-switch" aria-hidden="true" />
            <span>Quero receber lembretes por email</span>
          </label>

          <label className="checkbox-row lgpd-consent-row toggle-row">
            <input
              className="toggle-input"
              type="checkbox"
              checked={formData.lgpdConsent}
              onChange={onChange('lgpdConsent')}
              required
            />
            <span className="toggle-switch" aria-hidden="true" />
            <span>
              Concordo com a <Link to="/privacidade" target="_blank" rel="noreferrer">Política de Privacidade e Termos LGPD</Link>
            </span>
          </label>

          <button type="submit" className="cta-btn">Confirmar presença</button>
        </form>

        {rsvpReceipt && (
          <section className="receipt-card" aria-live="polite">
            <p className="festify-eyebrow">Comprovante de presenca</p>
            <h3>Protocolo {rsvpReceipt.protocol}</h3>
            <p>
              <strong>Convidado:</strong> {rsvpReceipt.guestName}<br />
              <strong>Status:</strong> {rsvpReceipt.status}<br />
              <strong>Total de pessoas:</strong> {rsvpReceipt.attendees}<br />
              <strong>Evento:</strong> {eventData.title}<br />
              <strong>Data:</strong> {formatDate(eventData.event_date)}
            </p>
            {qrCodeUrl && (
              <div className="receipt-qr-wrap">
                <img src={qrCodeUrl} alt={`QR Code do protocolo ${rsvpReceipt.protocol}`} loading="lazy" />
              </div>
            )}
            <div className="receipt-actions">
              <button
                type="button"
                className="ghost-btn receipt-download-btn"
                onClick={handleDownloadReceiptPdf}
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? 'Gerando PDF...' : 'Baixar comprovante em PDF'}
              </button>
            </div>
            <p className="input-helper">Apresente este protocolo/QR na entrada do evento.</p>
          </section>
        )}
      </section>

      <footer className="invite-footer">
        <p>✅ {summary.confirmed} confirmados | ⏳ {summary.pending} pendentes | ❌ {summary.declined} recusados</p>
      </footer>

      {showConfetti && <div className="confetti-burst" aria-hidden="true">🎉 🎊 🎉 🎊 🎉</div>}
    </main>
  );
}
